import { noticeError } from "~/monitor.client";
import reporter, { isReportableError } from "~/reporter";

// React errors #418, #419, #421-#425 are all *recoverable* hydration errors:
// when the server-rendered markup doesn't match what the client would render
// (or a Suspense boundary can't finish on the server), React discards the
// mismatched subtree/root and re-renders it on the client, so the page still
// ends up correct. See https://react.dev/errors/418 (and neighboring codes).
// Honeybadger fault 133865151 (#418) confirmed these are not actionable here:
// the same message showed up across totally unrelated pages (checkout,
// tax-policy, faq, random product pages) and browsers (desktop/mobile Safari,
// Chrome, Firefox), with no reproducible app-level defect -- consistent with
// third-party scripts/extensions/in-app browsers mutating the DOM before
// hydration runs, not a bug in our components.
const RECOVERABLE_HYDRATION_ERROR_CODES = [418, 419, 421, 422, 423, 424, 425];

const isRecoverableHydrationError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return (
    RECOVERABLE_HYDRATION_ERROR_CODES.some((code) =>
      message.includes(`errors/${code}`),
    ) ||
    /did not finish this Suspense boundary|didn't match the client|Text content does not match server-rendered HTML|received an update before it finished hydrating|React was able to recover by instead client rendering|received an early update, before anything was able hydrate/.test(
      message,
    )
  );
};

// Dropping these entirely would mean a future *app-level* hydration bug (a
// real Date.now()/locale mismatch in some component, say) goes completely
// undetected. Sample a slice of them to New Relic instead of Honeybadger --
// out of the alerting path, but still enough volume that a genuine regression
// still shows up as a spike.
const HYDRATION_SAMPLE_RATE = 0.05;

// hydrateRoot needs an onRecoverableError; without one, React's default routes
// every recoverable error through the global reportError() -> window.onerror,
// where Honeybadger records these benign hydration recoveries as fatal Errors.
// Drop them and report the rest through the filtered reporter.
export const onRecoverableError = (
  error: unknown,
  errorInfo: { componentStack?: string },
): void => {
  if (isRecoverableHydrationError(error)) {
    if (Math.random() < HYDRATION_SAMPLE_RATE) {
      noticeError(error, { componentStack: errorInfo?.componentStack });
    }
    return;
  }
  if (!isReportableError(error)) return;

  reporter.captureException(error, undefined, {
    componentStack: errorInfo?.componentStack,
  });
};
